import axios from 'axios';
import config from '../config';
import { useAuthStore } from '../stores/auth.store';

// Create axios instance for ContractLens API
const contractLensApi = axios.create({
  baseURL: config.contractLensApiUrl,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token and customer ID interceptor
contractLensApi.interceptors.request.use(
  (cfg) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      cfg.headers.Authorization = `Bearer ${token}`;
    }

    // Add customer ID from auth store
    const customer = useAuthStore.getState().customer;
    if (customer?.id) {
      cfg.headers['X-Customer-Id'] = customer.id;
    }

    return cfg;
  },
  (error) => Promise.reject(error)
);

// Types
export interface ContractStats {
  totalContracts: number;
  activeContracts: number;
  expiringSoon: number;
  alertsNext30Days: number;
  recentUploads: number;
  categories: string[];
  areas: string[];
}

export interface Contract {
  id: string;
  title: string;
  description?: string;
  companyName: string;
  organizationNumber?: string;
  supplierType?: string;
  category: string;
  area: string;
  startDate?: string;
  endDate?: string;
  contractValue?: number;
  currency?: string;
  status: string;
  confidenceScore?: number;
  tags: string[];
  createdAt: string;
}

export interface AlertSummary {
  id: string;
  type: string;
  title: string;
  description?: string;
  alertDate: string;
  isDismissed: boolean;
  companyName: string;
  contractId: string;
}

export interface AlertsResponse {
  alerts: AlertSummary[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface AlertsSummaryResponse {
  totalUpcoming: number;
  next7Days: number;
  next30Days: number;
  next90Days: number;
  expiryWarnings: number;
  paymentDue: number;
  noticePeriods: number;
  renewalReminders: number;
}

export interface CustomerSettings {
  customerId: string;
  logoUrl: string | null;
  logoFileName: string | null;
  hasLogo: boolean;
}

export interface AdminCustomerSettings {
  customerId: string;
  hasLogo: boolean;
  logoFileName: string | null;
  pageBackgroundColor: string | null;
  navbarBackgroundColor: string | null;
  navbarTextColor: string | null;
  pageTextColor: string | null;
  pageHeadingColor: string | null;
  bannerBackgroundColor: string | null;
}

export interface UpdateColorsRequest {
  pageBackgroundColor: string | null;
  navbarBackgroundColor: string | null;
  navbarTextColor: string | null;
  pageTextColor: string | null;
  pageHeadingColor: string | null;
  bannerBackgroundColor: string | null;
}

export interface AdminArea {
  id: string;
  name: string;
  description: string | null;
  isPublic: boolean;
  allowPublicUpload: boolean;
  createdAt: string;
  userCount: number;
  contractCount: number;
}

export interface CreateAreaRequest {
  name: string;
  description?: string;
  isPublic: boolean;
  allowPublicUpload: boolean;
}

export interface UpdateAreaRequest {
  name: string;
  description?: string;
  isPublic: boolean;
  allowPublicUpload: boolean;
}

export const contractLensService = {
  // Get contract statistics
  async getStats(): Promise<ContractStats> {
    const response = await contractLensApi.get<ContractStats>('/api/contracts/stats');
    return response.data;
  },

  // Get recent contracts
  async getRecentContracts(count = 5): Promise<Contract[]> {
    const response = await contractLensApi.get<Contract[]>('/api/contracts/recent', {
      params: { count },
    });
    return response.data;
  },

  // Get upcoming alerts
  async getAlerts(days = 30, page = 1, pageSize = 10): Promise<AlertsResponse> {
    const response = await contractLensApi.get<AlertsResponse>('/api/alerts', {
      params: { days, page, pageSize },
    });
    return response.data;
  },

  // Get alert summary/counts
  async getAlertsSummary(): Promise<AlertsSummaryResponse> {
    const response = await contractLensApi.get<AlertsSummaryResponse>('/api/alerts/summary');
    return response.data;
  },

  // Search contracts
  async searchContracts(params: {
    query?: string;
    area?: string;
    category?: string;
    status?: string;
    smartView?: string;
    page?: number;
    pageSize?: number;
  }): Promise<{ items: Contract[]; totalCount: number; page: number; totalPages: number }> {
    const response = await contractLensApi.get('/api/contracts', { params });
    return response.data;
  },

  // Dismiss an alert
  async dismissAlert(alertId: string): Promise<void> {
    await contractLensApi.post(`/api/alerts/${alertId}/dismiss`);
  },

  // Get customer settings (including logo URL)
  async getCustomerSettings(): Promise<CustomerSettings> {
    const response = await contractLensApi.get<CustomerSettings>('/api/customer/settings');
    return response.data;
  },

  // === Admin endpoints (GlobalAdmin only) ===

  async getAdminCustomerSettings(customerId: string): Promise<AdminCustomerSettings> {
    const response = await contractLensApi.get<AdminCustomerSettings>(
      `/api/admin/customers/${customerId}/settings`
    );
    return response.data;
  },

  async updateAdminCustomerColors(customerId: string, request: UpdateColorsRequest): Promise<AdminCustomerSettings> {
    const response = await contractLensApi.put<AdminCustomerSettings>(
      `/api/admin/customers/${customerId}/settings/colors`,
      request
    );
    return response.data;
  },

  async uploadAdminCustomerLogo(customerId: string, file: File): Promise<{ logoFileName: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await contractLensApi.post<{ logoFileName: string }>(
      `/api/admin/customers/${customerId}/settings/logo`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  async getAdminCustomerLogoBlob(customerId: string): Promise<string> {
    const response = await contractLensApi.get(
      `/api/admin/customers/${customerId}/settings/logo`,
      { responseType: 'blob' }
    );
    return URL.createObjectURL(response.data);
  },

  async deleteAdminCustomerLogo(customerId: string): Promise<void> {
    await contractLensApi.delete(`/api/admin/customers/${customerId}/settings/logo`);
  },

  // === Admin area endpoints (GlobalAdmin only) ===

  async getAdminCustomerAreas(customerId: string): Promise<AdminArea[]> {
    const response = await contractLensApi.get<AdminArea[]>(
      `/api/admin/customers/${customerId}/areas`
    );
    return response.data;
  },

  async createAdminCustomerArea(customerId: string, request: CreateAreaRequest): Promise<AdminArea> {
    const response = await contractLensApi.post<AdminArea>(
      `/api/admin/customers/${customerId}/areas`,
      request
    );
    return response.data;
  },

  async updateAdminCustomerArea(customerId: string, areaId: string, request: UpdateAreaRequest): Promise<AdminArea> {
    const response = await contractLensApi.put<AdminArea>(
      `/api/admin/customers/${customerId}/areas/${areaId}`,
      request
    );
    return response.data;
  },

  async deleteAdminCustomerArea(customerId: string, areaId: string): Promise<void> {
    await contractLensApi.delete(`/api/admin/customers/${customerId}/areas/${areaId}`);
  },
};

export default contractLensService;
